import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, TrendingUp, MousePointerClick, Calendar, ShoppingBag, ExternalLink, Mail, Copy, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AffiliateAnalyticsPage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentClicks, setRecentClicks] = useState([]);
  const [subscribers, setSubscribers] = useState({ total: 0, subscribers: [] });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, clicksRes, subsRes] = await Promise.all([
        axios.get(`${API}/affiliate/stats`),
        axios.get(`${API}/affiliate/clicks?limit=20`),
        axios.get(`${API}/admin/subscribers`)
      ]);
      setStats(statsRes.data);
      setRecentClicks(clicksRes.data);
      setSubscribers(subsRes.data);
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  const username = user?.username;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
    } else if (username !== 'meetmrfashion') {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [isAuthenticated, username, navigate]);

  useEffect(() => {
    if (isAuthenticated && username === 'meetmrfashion') {
      fetchData();
    }
  }, [isAuthenticated, username, fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-gray-400">Loading analytics...</div>
      </div>
    );
  }

  const sortedBrands = stats && stats.clicks_by_brand
    ? Object.entries(stats.clicks_by_brand).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <div className="min-h-screen pt-20 bg-[#0a0a0a]" data-testid="affiliate-analytics-page">
      {/* Header */}
      <div className="py-12 md:py-16 px-6 md:px-12 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <BarChart3 className="w-8 h-8 text-[#C41E3A]" />
            <p className="text-xs uppercase tracking-[0.2em] text-[#C41E3A]">Rakuten Affiliate</p>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif italic text-white">Analytics Dashboard</h1>
          <p className="text-gray-400 mt-4">Track your affiliate link performance and earnings potential</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
            {/* Total Clicks */}
            <div className="bg-[#1c1c1c] border border-white/5 p-6 rounded-sm">
              <div className="flex items-center gap-2 text-gray-400 mb-2">
                <MousePointerClick className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wider">Total Clicks</span>
              </div>
              <p className="text-3xl md:text-4xl font-serif text-white">{stats?.total_clicks || 0}</p>
            </div>

            {/* Today */}
            <div className="bg-[#1c1c1c] border border-white/5 p-6 rounded-sm">
              <div className="flex items-center gap-2 text-gray-400 mb-2">
                <Calendar className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wider">Today</span>
              </div>
              <p className="text-3xl md:text-4xl font-serif text-white">{stats?.clicks_today || 0}</p>
            </div>

            {/* This Week */}
            <div className="bg-[#1c1c1c] border border-white/5 p-6 rounded-sm">
              <div className="flex items-center gap-2 text-gray-400 mb-2">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wider">This Week</span>
              </div>
              <p className="text-3xl md:text-4xl font-serif text-white">{stats?.clicks_this_week || 0}</p>
            </div>

            {/* This Month */}
            <div className="bg-[#1c1c1c] border border-white/5 p-6 rounded-sm">
              <div className="flex items-center gap-2 text-gray-400 mb-2">
                <ShoppingBag className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wider">This Month</span>
              </div>
              <p className="text-3xl md:text-4xl font-serif text-white">{stats?.clicks_this_month || 0}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Top Performing Looks */}
            <div className="bg-[#1c1c1c] border border-white/5 p-6 rounded-sm">
              <h2 className="text-xl font-serif text-white mb-6 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#C41E3A]" />
                Top Performing Looks
              </h2>
              {stats?.top_looks?.length > 0 ? (
                <div className="space-y-4">
                  {stats.top_looks.map((look, idx) => (
                    <div key={look.look_id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-3">
                        <span className="text-[#C41E3A] font-bold">#{idx + 1}</span>
                        <span className="text-white text-sm">{look.title}</span>
                      </div>
                      <span className="text-gray-400 text-sm">{look.clicks} clicks</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No clicks recorded yet</p>
              )}
            </div>

            {/* Clicks by Brand */}
            <div className="bg-[#1c1c1c] border border-white/5 p-6 rounded-sm">
              <h2 className="text-xl font-serif text-white mb-6 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#C41E3A]" />
                Clicks by Brand
              </h2>
              {sortedBrands.length > 0 ? (
                <div className="space-y-4">
                  {sortedBrands.map(([brand, clicks]) => (
                      <div key={brand} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                        <span className="text-white text-sm">{brand}</span>
                        <div className="flex items-center gap-3">
                          <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#C41E3A] rounded-full"
                              style={{ width: `${Math.min((clicks / stats.total_clicks) * 100, 100)}%` }}
                            />
                          </div>
                          <span className="text-gray-400 text-sm w-12 text-right">{clicks}</span>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No brand data yet</p>
              )}
            </div>
          </div>

          {/* Recent Clicks */}
          <div className="mt-8 bg-[#1c1c1c] border border-white/5 p-6 rounded-sm">
            <h2 className="text-xl font-serif text-white mb-6 flex items-center gap-2">
              <MousePointerClick className="w-5 h-5 text-[#C41E3A]" />
              Recent Clicks
            </h2>
            {recentClicks.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-400 text-xs uppercase tracking-wider border-b border-white/10">
                      <th className="pb-3">Look</th>
                      <th className="pb-3">Brand</th>
                      <th className="pb-3">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentClicks.map((click) => (
                      <tr key={click.id} className="border-b border-white/5 last:border-0">
                        <td className="py-3 text-white text-sm">{click.look_title || 'Unknown'}</td>
                        <td className="py-3 text-gray-400 text-sm">{click.brand || '-'}</td>
                        <td className="py-3 text-gray-500 text-sm">
                          {new Date(click.clicked_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No clicks recorded yet</p>
            )}
          </div>

          {/* Rakuten Setup Info */}
          <div className="mt-8 bg-gradient-to-r from-[#C41E3A]/10 to-transparent border border-[#C41E3A]/20 p-6 rounded-sm">
            <h3 className="text-lg font-serif text-white mb-3 flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-[#C41E3A]" />
              Rakuten Advertising Setup
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              To start earning commissions, you need to set up affiliate links for each look:
            </p>
            <ol className="text-gray-400 text-sm space-y-2 list-decimal list-inside">
              <li>Sign up at <a href="https://rakutenadvertising.com" target="_blank" rel="noopener noreferrer" className="text-[#C41E3A] hover:underline">rakutenadvertising.com</a></li>
              <li>Apply to luxury brand programs (Louis Vuitton, Gucci, Hermès, etc.)</li>
              <li>Generate affiliate links for each product</li>
              <li>Add the links to your looks using the admin panel or API</li>
            </ol>
          </div>

          {/* Subscribers Section */}
          <SubscribersSection subscribers={subscribers} />
        </div>
      </div>
    </div>
  );
};

const SubscribersSection = ({ subscribers }) => {
  const copyAllEmails = async () => {
    const emails = subscribers.subscribers.map(s => s.email).join('\n');
    try {
      await navigator.clipboard.writeText(emails);
      toast.success(`${subscribers.total} email(s) copied to clipboard`);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = emails;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success(`${subscribers.total} email(s) copied to clipboard`);
    }
  };

  return (
    <div className="mt-8 bg-[#1c1c1c] border border-white/5 p-6 rounded-sm" data-testid="subscribers-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-serif text-white flex items-center gap-2">
          <Mail className="w-5 h-5 text-[#C41E3A]" />
          Subscribers
        </h2>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-2 px-3 py-1.5 bg-[#C41E3A]/10 border border-[#C41E3A]/30 rounded-sm">
            <Users className="w-4 h-4 text-[#C41E3A]" />
            <span className="text-sm text-white font-medium" data-testid="subscriber-count">{subscribers.total}</span>
          </span>
          {subscribers.total > 0 && (
            <button
              onClick={copyAllEmails}
              className="flex items-center gap-2 px-3 py-1.5 border border-white/10 rounded-sm text-gray-400 hover:text-white hover:border-white/20 transition-colors text-xs uppercase tracking-wider"
              data-testid="copy-emails-btn"
            >
              <Copy className="w-3.5 h-3.5" />
              Copy All Emails
            </button>
          )}
        </div>
      </div>

      {subscribers.subscribers.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-gray-400 text-xs uppercase tracking-wider border-b border-white/10">
                <th className="pb-3">#</th>
                <th className="pb-3">Email</th>
                <th className="pb-3">Subscribed</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.subscribers.map((sub, idx) => (
                <tr key={sub.id || idx} className="border-b border-white/5 last:border-0">
                  <td className="py-3 text-gray-600 text-sm">{idx + 1}</td>
                  <td className="py-3 text-white text-sm">{sub.email}</td>
                  <td className="py-3 text-gray-500 text-sm">
                    {new Date(sub.subscribed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-500 text-center py-8">No subscribers yet. Share your site to grow your audience!</p>
      )}
    </div>
  );
};

export default AffiliateAnalyticsPage;
